import { error as logErrorToConsole } from '@ms/yammer-console-logging';
import {
  enqueueTelemetryEvents,
  getTelemetryClientConfig,
  getTelemetryEventQueueLength,
} from '@ms/yammer-telemetry-store';

import type { LogEvent, LogEvents, TelemetryEvent } from '../events';
import { applyMiddleware } from '../middleware';

import { getReportLogEventsInput } from './getReportLogEventsInput';
import { getThrottledReportLogEventsInBatch } from './reportInBatches';
import { reportLogEventsNow } from './reportTelemetryEventsToServer';

const eventsAllowedForReportNow = ['AcquireTokenAuthError'];
const logEventCountFlushThreshold = 60;
const logEventTypes: LogEvent['type'][] = ['Error', 'Info', 'Performance'];

const getQueuedLogEventCount = () =>
  logEventTypes.reduce((total, logEventType) => total + getTelemetryEventQueueLength(logEventType), 0);

type EnqueueAndThrottleReportLogEvents = <T extends TelemetryEvent>(
  eventType: T['type'],
  events: T[],
  /**
   * Will skip applying middleware to events if true. Use this option if your events have already been processed by middleware.
   */
  disableMiddleware?: boolean
) => Promise<void>;
export const enqueueAndThrottleReportLogEvents: EnqueueAndThrottleReportLogEvents = async <T extends TelemetryEvent>(
  eventType: T['type'],
  events: T[],
  disableMiddleware = false
) => {
  const clientConfig = getTelemetryClientConfig();
  if (!clientConfig) {
    return;
  }

  const processedEvents = disableMiddleware ? events : await Promise.all(events.map(applyMiddleware));
  enqueueTelemetryEvents<T>(eventType, processedEvents as T[]);
  const throttledReportAction = getThrottledReportLogEventsInBatch(
    // istanbul ignore next
    getTelemetryClientConfig()?.eventReportIntervalInMilliseconds ?? 0
  );

  void throttledReportAction();
  if (clientConfig.enableLogPagehideDelivery && getQueuedLogEventCount() >= logEventCountFlushThreshold) {
    void throttledReportAction.flush();
  }
};

type ReportLogEvent = (event: LogEvent) => void;
export const reportLogEvent: ReportLogEvent = (event) => {
  const clientConfig = getTelemetryClientConfig();
  if (!clientConfig) {
    return;
  }

  void enqueueAndThrottleReportLogEvents(event.type, [event], true);
};

type ReportLogEventNow = (event: LogEvent) => Promise<void>;

export const reportLogEventNow: ReportLogEventNow = async (event) => {
  const clientConfig = getTelemetryClientConfig();
  if (!clientConfig) {
    return;
  }

  const logEvents: LogEvents = {
    errorEvents: event.type === 'Error' ? [event] : [],
    infoEvents: event.type === 'Info' ? [event] : [],
    performanceEvents: event.type === 'Performance' ? [event] : [],
  };
  if (!eventsAllowedForReportNow.includes(event.name)) {
    logErrorToConsole('Event not allowed for report now');

    return;
  }

  const reportLogEventInput = await getReportLogEventsInput(logEvents);
  await reportLogEventsNow(reportLogEventInput);
};
